import { HttpBackend, HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, from, switchMap, throwError } from 'rxjs';

import { AppConfig } from '@env/app-config';
import { PORTAL_CONSENT_REQUIRED, PortalErrorBody } from '../models/portal-comun.model';

/** Valores admitidos en la query string del portal. */
export type PortalParams = Record<string, string | number | boolean | null | undefined>;

/** Lo único que la puerta HTTP necesita de la sesión. */
export interface PortalAuthLike {
  handleSessionLost(): void;
  requireConsent(): void;
}

/**
 * Buzón entre la puerta HTTP y la sesión.
 *
 * <p>Existe para romper el ciclo: `PortalAuthService` llama al API y el API tiene que avisar a la
 * sesión cuando muere. Un `inject()` cruzado crearía una dependencia circular de módulos ES; este
 * buzón la convierte en un registro de una sola dirección — `PortalAuthService` se apunta al
 * construirse y el API solo dispara eventos. Si nadie se registró (pantalla de ingreso, antes de
 * que exista sesión) no pasa nada: no hay sesión que perder.</p>
 */
@Injectable({ providedIn: 'root' })
export class PortalSessionEvents {
  private manejador: PortalAuthLike | null = null;

  register(manejador: PortalAuthLike): void {
    this.manejador = manejador;
  }

  sessionLost(): void {
    this.manejador?.handleSessionLost();
  }

  consentRequired(): void {
    this.manejador?.requireConsent();
  }
}

/**
 * ÚNICA puerta HTTP del Portal de Familias.
 *
 * <p>El portal no usa el interceptor de partner-core: su identidad no es un usuario del sistema
 * sino una `portal_account`, y su credencial no es un `Authorization: Bearer` sino dos cookies
 * <strong>HttpOnly</strong> (`pat` y `prt`) que el JavaScript no puede leer ni escribir. Por eso
 * cada llamada va con {@code withCredentials: true} y por eso el «renovar y reintentar» vive aquí
 * y no en un interceptor global (registrarlo obligaría a tocar `app.config.ts`, que es del
 * integrador).</p>
 *
 * <p>Dos reglas que TODAS las pantallas heredan sin escribir una línea:</p>
 * <ul>
 *   <li><strong>401</strong> → un único {@code POST /auth/refresh} y se reintenta la llamada. Si
 *       vuelve a fallar, la sesión murió de verdad: se manda a la pantalla de ingreso con
 *       {@code ?expired=1}. El reintento es único a propósito: dos 401 seguidos con cookie
 *       renovada significan que el backend ya no reconoce la sesión.</li>
 *   <li><strong>409 {@code PORTAL_CONSENT_REQUIRED}</strong> → la empresa publicó una política
 *       nueva; el portal entero queda bloqueado hasta que se acepte, así que se navega al paso 3
 *       del ingreso en vez de mostrar un error que nadie sabría resolver.</li>
 * </ul>
 */
@Injectable({ providedIn: 'root' })
export class PortalApiService {
  /**
   * HttpClient SIN los interceptores globales del back-office (authInterceptor redirige a /auth/login
   * ante cualquier 401 y companyContextInterceptor mete X-Company-Id): el portal tiene su propia
   * identidad y su propio refresh, así que habla directo con el backend (HttpBackend).
   */
  private readonly http = new HttpClient(inject(HttpBackend));
  private readonly eventos = inject(PortalSessionEvents);

  readonly baseUrl = `${AppConfig.apiUrl}/api/eternium/portal`;

  /** Renovación en curso, compartida: cinco llamadas en paralelo no disparan cinco refresh. */
  private renovacion: Promise<boolean> | null = null;

  /** URL absoluta — para abrir descargas en una pestaña nueva. */
  url(path: string): string {
    return `${this.baseUrl}${path.startsWith('/') ? path : `/${path}`}`;
  }

  get<T>(path: string, params?: PortalParams): Observable<T> {
    return this.ejecutar(path, () =>
      this.http.get<T>(this.url(path), { params: this.toParams(params), withCredentials: true }));
  }

  post<T>(path: string, body?: unknown): Observable<T> {
    return this.ejecutar(path, () =>
      this.http.post<T>(this.url(path), body ?? {}, { withCredentials: true }));
  }

  put<T>(path: string, body?: unknown): Observable<T> {
    return this.ejecutar(path, () =>
      this.http.put<T>(this.url(path), body ?? {}, { withCredentials: true }));
  }

  /** Multipart (adjuntos de solicitudes): sin `Content-Type` manual, lo pone el navegador. */
  postForm<T>(path: string, form: FormData): Observable<T> {
    return this.ejecutar(path, () =>
      this.http.post<T>(this.url(path), form, { withCredentials: true }));
  }

  /** Descargas dentro de la app (PDF de historial, comprobantes, fotos de mantenimiento). */
  blob(path: string, params?: PortalParams): Observable<Blob> {
    return this.ejecutar(path, () =>
      this.http.get(this.url(path), {
        params: this.toParams(params),
        withCredentials: true,
        responseType: 'blob',
      }));
  }

  /**
   * Envuelve la llamada con el tratamiento de 401 y 409. Recibe una FÁBRICA y no un observable ya
   * creado porque el reintento tiene que emitir una petición nueva, no resuscribirse a la vieja.
   */
  private ejecutar<T>(path: string, fabrica: () => Observable<T>): Observable<T> {
    return fabrica().pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 409 && this.codigoDe(error) === PORTAL_CONSENT_REQUIRED) {
          this.eventos.consentRequired();
          return throwError(() => error);
        }
        if (error.status !== 401 || this.esPublico(path)) {
          return throwError(() => error);
        }
        return from(this.renovar()).pipe(
          switchMap(ok => {
            if (!ok) {
              this.eventos.sessionLost();
              return throwError(() => error);
            }
            return fabrica().pipe(
              catchError((segundo: HttpErrorResponse) => {
                if (segundo.status === 401) {
                  this.eventos.sessionLost();
                }
                return throwError(() => segundo);
              }),
            );
          }),
        );
      }),
    );
  }

  /** `/auth/**` y `/public/**` son anónimos: un 401 ahí no es una sesión caída. */
  private esPublico(path: string): boolean {
    return path.startsWith('/auth') || path.startsWith('/public');
  }

  /** Una sola renovación viva a la vez. */
  private renovar(): Promise<boolean> {
    if (!this.renovacion) {
      this.renovacion = new Promise<boolean>(resolve => {
        this.http.post(`${this.baseUrl}/auth/refresh`, {}, { withCredentials: true }).subscribe({
          next: () => resolve(true),
          error: () => resolve(false),
        });
      }).finally(() => {
        this.renovacion = null;
      }) as Promise<boolean>;
    }
    return this.renovacion;
  }

  /** Código de negocio del error, venga del handler del portal o de un `ApiResponse`. */
  codigoDe(error: HttpErrorResponse): string | null {
    const body = error.error as PortalErrorBody | string | null;
    if (!body || typeof body === 'string') {
      return null;
    }
    return body.code ?? null;
  }

  /** Mensaje que la pantalla puede mostrar tal cual; nunca `undefined`. */
  mensajeDe(error: HttpErrorResponse, porDefecto: string): string {
    const body = error.error as PortalErrorBody | string | null;
    if (typeof body === 'string' && body.trim()) {
      return body;
    }
    if (body && typeof body === 'object' && body.message) {
      return body.message;
    }
    return porDefecto;
  }

  private toParams(params?: PortalParams): HttpParams {
    let http = new HttpParams();
    if (!params) {
      return http;
    }
    for (const [clave, valor] of Object.entries(params)) {
      if (valor !== null && valor !== undefined && valor !== '') {
        http = http.set(clave, `${valor}`);
      }
    }
    return http;
  }

}
